import { useState } from 'react';

import { useUpdatePiecesMutation } from '@api/piece';
import {
  Container,
  ContainerRequest,
  Piece,
  PieceAction,
  PieceLocation,
  PieceLocationRequest,
  PieceType,
  Workflow,
} from '@rdTypes/apiContract';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { useSettings } from 'src/hooks/useSettings';
import { useGetContainerTypesQuery } from 'src/redux/api/container';
import { SortingOrder } from 'src/types/contractHelpers';
import { getContainerDefaultValues, getContainerTypeIdByWorkflow } from 'src/utils/container';
import { getPieceTypeByWorkflow, isPieceInLocker } from 'src/utils/piece';

import { showApiErrorToast } from '@utils/apiErrorToast';

export type LocationStateChange = {
  itemSpot?: string | number;
  storageGroupData?: ContainerRequest & Partial<Pick<Container, 'nextOrderNo'>>;
  lockerBankId?: number;
  lockerCell?: string;
  lockerCellSize?: string;
};

export type LocationState = {
  itemType: PieceType;
} & LocationStateChange;

export type LocationLockersError = {
  lockerBankId?: string;
  lockerCell?: string;
};

const getLocationValuesFromPiece = (stateWorkflow: Workflow, location?: PieceLocation) => ({
  itemType: getPieceTypeByWorkflow(stateWorkflow),
  itemSpot: location?.packageSpot || '',
  storageGroupData: {
    containerTypeId: location?.container.containerType.id,
  } as ContainerRequest,
  lockerBankId: +(location?.container.lockerBankId || 0) || undefined,
  lockerCell: location?.lockerCell || undefined,
  lockerCellSize: location?.lockerCellSize || undefined,
});

export const useLocationId = (
  piece: Piece,
  onChange: (piece: Piece) => void,
  onClose: () => void,
  context?: 'capture',
) => {
  const [stateWorkflow, setStateWorkflow] = useState<Workflow>(piece.workflow);

  const location = piece.pieceLocation;
  const setLastLockerSize = useLocalStorage<string>('rd_last_locker_size', '')[1];
  const { data: containerTypes = [] } = useGetContainerTypesQuery();
  const receiveSettings = useSettings(['LockersWorkflowEnable', 'LockerReservationEnabled', 'ReceiveSortOrder']) as {
    LockerReservationEnabled: boolean;
    LockersWorkflowEnable: boolean;
    ReceiveSortOrder: SortingOrder;
  };

  const [spot, setSpot] = useState<LocationState>(getLocationValuesFromPiece(stateWorkflow, location));

  const onChangeWorkflow = (nextWorkflow: Workflow) => {
    setStateWorkflow(nextWorkflow);
    if (piece.workflow === nextWorkflow) {
      setSpot(getLocationValuesFromPiece(nextWorkflow, location));
    } else {
      setSpot({
        ...spot,
        storageGroupData: getContainerDefaultValues(containerTypes, receiveSettings.ReceiveSortOrder, nextWorkflow),
      });
    }
  };

  const onChangeSpot = (params: LocationStateChange) =>
    setSpot({
      itemType: spot.itemType,
      ...params,
    });

  const saveDisabled =
    isPieceInLocker(piece) ||
    (receiveSettings.LockersWorkflowEnable &&
      receiveSettings.LockerReservationEnabled &&
      stateWorkflow === Workflow.Lockers &&
      (!spot.lockerBankId || !(spot.lockerCell || spot.lockerCellSize)));

  const [error, setError] = useState<LocationLockersError>({});

  const [updatePieces, { isLoading }] = useUpdatePiecesMutation();

  const onSaveLocation = async () => {
    const request: PieceLocationRequest = {};

    if (stateWorkflow === Workflow.Lockers) {
      const containerTypeId = containerTypes.find((container) => container.workflow === Workflow.Lockers)?.id;
      if (!containerTypeId) return;
      request.container = {
        containerTypeId,
      };
      if (receiveSettings.LockerReservationEnabled) {
        if (!spot.lockerBankId || !(spot.lockerCell || spot.lockerCellSize)) {
          const errors: LocationLockersError = {};
          if (!spot.lockerBankId) {
            errors.lockerBankId = 'Locker Bank is required';
          }
          if (!spot.lockerCell) {
            errors.lockerCell = 'Locker Cell is required';
          }

          setError(errors);
          return;
        }
        request.lockerBankId = spot.lockerBankId;
        if (spot.lockerCell) {
          request.lockerCell = spot.lockerCell;
        }
        if (spot.lockerCellSize) {
          request.lockerCellSize = spot.lockerCellSize;
        }
      }
    } else {
      if (!spot.storageGroupData) return;
      if (spot.itemType === PieceType.Package) {
        request.packageSpot = spot.itemSpot as string;
      }

      request.container = {
        containerTypeId:
          spot.storageGroupData.containerTypeId || getContainerTypeIdByWorkflow(containerTypes, stateWorkflow),
        id: spot.storageGroupData.id,
        containerNumber: spot.storageGroupData.containerNumber || '',
        sortOrder: spot.storageGroupData.sortOrder,
      };

      if (stateWorkflow === Workflow.DateReceived) {
        request.container.createdDate = spot.storageGroupData.createdDate;
      }
      request.orderNo = 0;
    }

    try {
      const result = await updatePieces({
        action: PieceAction.Move,
        pieces: [
          {
            id: piece.id,
            pieceLocation: request,
          },
        ],
        context,
      }).unwrap();

      if (spot.lockerCellSize) {
        setLastLockerSize(spot.lockerCellSize);
      }
      onChange(result[0]);
      onClose();
    } catch (err) {
      showApiErrorToast(err);
    }
  };

  return {
    error,
    spot,
    stateWorkflow,
    onChangeWorkflow,
    setSpot: onChangeSpot,
    onSaveLocation,
    isLoading,
    saveDisabled,
    nextOrderNo: spot.storageGroupData?.nextOrderNo,
  };
};
